<template>
  <div>
    <v-dialog
      lazy
      scrollable
      persistent
      :value="show"
      :width="800"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      data-cy="create-edit-branding-dialog"
      @input="(v) => $emit('update:show', v)"
    >
      <v-card
        v-if="branding"
        :key="dialogCardRenderKey"
      >
        <v-card-title class="pb-1">
          <v-layout column>
            <v-layout align-center>
              <span class="headline">
                {{
                  isNewBranding
                    ? $t('brandingDialogTitleCreate')
                    : $t('brandingDialogTitleEdit')
                }}<span v-if="!isNewBranding">: {{ branding.name }}</span>
              </span>
              <v-btn
                class="ml-auto mr-0 my-0"
                flat
                icon
                :title="$t('close')"
                @click="askAbort()"
              >
                <v-icon>close</v-icon>
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-title>
        <v-divider class="mb-2" />
        <v-card-text class="py-0 px-0">
          <div class="pl-3 pr-5 pb-3">
            {{ $t('tvtemplatesPleaseCreateBrandingHint') }}
          </div>
          <v-stepper
            v-model="step"
            alt-labels
            style="box-shadow: none"
          >
            <v-stepper-header style="box-shadow: none">
              <!-- Step 1 -->
              <v-stepper-step
                editable
                step="1"
              >
                {{ $t('brandingDialogStep1') }}
              </v-stepper-step>
              <v-divider />
              <!-- Step 2 -->
              <v-stepper-step
                :editable="step1Valid"
                step="2"
              >
                {{ $t('brandingDialogStep2') }}
              </v-stepper-step>
              <v-divider />
              <!-- Step 3 -->
              <v-stepper-step
                :editable="step2Valid"
                step="3"
              >
                {{ $t('brandingDialogStep3') }}
              </v-stepper-step>
            </v-stepper-header>
            <v-stepper-items>
              <!-- Step 1 Content -->
              <v-stepper-content
                step="1"
                class="pa-3"
              >
                <v-layout column>
                  <v-layout
                    align-center
                    wrap
                  >
                    <v-flex
                      xs12
                      sm8
                    >
                      <!-- Name -->
                      <v-text-field
                        ref="namefield"
                        v-model="branding.name"
                        :rules="[
                          (v) =>
                            v.trim().length === 0 ? $t('tooShort') : true,
                          (v) =>
                            v.length > brandingNameMaxLength
                              ? $t('tooLong')
                              : true,
                        ]"
                        class="mb-2 mx-3"
                        :label="$t('brandingDialogName')"
                        :counter="brandingNameMaxLength"
                        data-cy="input-branding-name"
                      />
                    </v-flex>
                    <v-flex
                      xs12
                      sm4
                    >
                      <!-- Branch -->
                      <v-select
                        v-model="branding.branch_id"
                        :items="branches"
                        :label="$t('brandingBranch')"
                        item-text="name"
                        item-value="id"
                        class="mb-2 mx-3"
                        data-cy="input-branding-branch"
                      />
                    </v-flex>
                  </v-layout>
                  <v-layout
                    align-center
                    wrap
                  >
                    <branding-colorfield
                      index="1"
                      class="mr-3 mt-3"
                      color-picker-id="brandingColorPrimary"
                      :label="$t('brandingColorPrimary')"
                      :color.sync="branding.color_primary"
                      ignore-alpha
                      data-cy="input-branding-color-primary"
                      @changed="onPrimaryColorChanged"
                    />
                    <branding-colorfield
                      index="2"
                      class="mr-3 mt-3"
                      color-picker-id="brandingColorSecondary"
                      :label="$t('brandingColorSecondary')"
                      :color.sync="branding.color_secondary"
                      ignore-alpha
                      data-cy="input-branding-color-secondary"
                      @changed="onSecondaryColorChanged"
                    />
                    <v-btn
                      flat
                      icon
                      large
                      color="grey darken-2 ma-0"
                      :title="$t('brandingSwapColors')"
                      data-cy="swap-colors-primary-secondary-btn"
                      @click="
                        swapBrandingColors('color_primary', 'color_secondary');
                        onPrimaryColorChanged();
                        onSecondaryColorChanged();
                      "
                    >
                      <v-icon medium>
                        swap_horiz
                      </v-icon>
                    </v-btn>
                  </v-layout>
                </v-layout>
              </v-stepper-content>
              <!-- Step 2 Content -->
              <v-stepper-content
                step="2"
                class="pa-3"
              >
                <v-layout column>
                  <div class="text-xs-center grey--text text--darken-3 mb-4">
                    {{ $t('brandingLogoTransparentBackgroundHint') }}
                  </div>
                  <branding-logofield
                    :key="brandingLogoRenderKey"
                    class="mx-5 mb-2"
                    :logo.sync="branding.logo"
                    :label="$t('brandingLogo')"
                    :title="$t('brandingLogoUpload')"
                    @cropped="onBrandingImageCropped"
                  />
                </v-layout>
              </v-stepper-content>
              <!-- Step 3 Content -->
              <v-stepper-content
                step="3"
                class="pa-3"
              >
                <v-layout column>
                  <div class="grey--text text--darken-3 mb-4">
                    {{ $t('brandingDialogStep3Subtitle') }}
                  </div>
                  <!-- Background Color -->
                  <v-layout
                    class="mt-2 mb-3"
                    wrap
                  >
                    <!-- Background Color Preview (logo on top for contrast) -->
                    <v-flex
                      sm6
                      xs12
                      style="pointer-events: none"
                    >
                      <branding-field
                        :border="false"
                        :label="$t('brandingBackgroundColor')"
                        :class="{'mr-3': $vuetify.breakpoint.smAndUp}"
                        min-height="140px"
                        max-height="140px"
                      >
                        <v-layout
                          slot="field"
                          align-center
                          justify-center
                          :style="{
                            background: branding.color_background,
                          }"
                        >
                          <v-img
                            v-if="brandingLogoUrl"
                            ref="logo"
                            contain
                            max-width="45%"
                            max-height="80%"
                            :src="brandingLogoUrl"
                          />

                          <v-img
                            v-if="brandingBackgroundBranchImageUrl"
                            ref="logo"
                            contain
                            max-width="40%"
                            max-height="80%"
                            :style="{
                              'margin-left': !!brandingLogoUrl
                                ? '3%'
                                : 'initial',
                            }"
                            :src="brandingBackgroundBranchImageUrl"
                          />
                        </v-layout>
                      </branding-field>
                    </v-flex>
                    <!-- Background Color UI -->
                    <v-flex
                      sm6
                      xs12
                    >
                      <branding-colorfield
                        min-height="48px"
                        max-height="48px"
                        min-width="48px"
                        max-width="48px"
                        color-picker-id="brandingColorBackground"
                        :color.sync="branding.color_background"
                        :color-palette="brandingColorPalette"
                        :default-color="'#eeeeee'"
                        @changed="onBackgroundColorChanged"
                      />
                    </v-flex>
                  </v-layout>

                  <v-layout
                    class="mt-2 mb-3"
                    wrap
                  >
                    <!-- Headline Preview -->
                    <v-flex
                      sm6
                      xs12
                      style="pointer-events: none"
                    >
                      <branding-field
                        :border="false"
                        :label="$t('brandingText')"
                        :class="{'mr-3': $vuetify.breakpoint.smAndUp}"
                        min-height="120px"
                        max-height="120px"
                        :title="$t('brandingPreview')"
                      >
                        <v-layout
                          slot="field"
                          align-center
                          justify-center
                          :style="{
                            background: branding.color_background,
                            'font-family': previewedFont || branding.font_name,
                            color: branding.color_headline,
                          }"
                          style="font-size: 2.5em; font-weight: bold"
                        >
                          {{ $t('brandingHeadlinePreviewLabel').toUpperCase() }}
                        </v-layout>
                      </branding-field>
                    </v-flex>
                    <v-flex
                      sm6
                      xs12
                    >
                      <!-- Headline Color -->
                      <v-layout column>
                        <!-- Font -->
                        <branding-fontfield
                          class="grow-1"
                          :font-name.sync="branding.font_name"
                          :label="$t('brandingFont')"
                          :title="$t('brandingFont')"
                          @start-preview="
                            (fontName) => (previewedFont = fontName)
                          "
                          @stop-preview="() => (previewedFont = null)"
                        />
                        <branding-colorfield
                          no-label
                          class="mt-3"
                          min-height="48px"
                          max-height="48px"
                          min-width="48px"
                          max-width="48px"
                          menu-top
                          menu-right
                          color-picker-id="brandingColorHeadline"
                          :color.sync="branding.color_headline"
                          :color-palette="brandingColorPalette"
                          :default-color="'#000000'"
                          :title="$t('brandingColorHeadline')"
                        />
                      </v-layout>
                    </v-flex>
                  </v-layout>
                  <v-layout
                    class="mt-2 mb-3"
                    wrap
                  >
                    <!-- Pricewidget Preview -->
                    <v-flex
                      sm6
                      xs12
                      style="pointer-events: none"
                    >
                      <branding-field
                        :border="false"
                        :label="$t('brandingWidgetPriceField')"
                        :class="{'mr-3': $vuetify.breakpoint.smAndUp}"
                        :min-height="
                          branding.pricewidget_boxtype === 'NONE'
                            ? '150px'
                            : '230px'
                        "
                        :max-height="
                          branding.pricewidget_boxtype === 'NONE'
                            ? '150px'
                            : '230px'
                        "
                        :title="$t('brandingPreview')"
                      >
                        <v-layout
                          slot="field"
                          align-center
                          justify-center
                          :style="{background: branding.color_background}"
                        >
                          <div
                            class="boxtype"
                            :class="[
                              branding.pricewidget_boxtype.toLowerCase(),
                              branding.pricewidget_boxtype === 'NONE'
                                ? ''
                                : 'mt-4',
                            ]"
                            style="
                              min-width: 128px;
                              min-height: 128px;
                              line-height: 72px;
                              display: flex;
                              justify-content: center;
                              align-items: center;
                              font-weight: bold;
                              transform: scale(1.25);
                            "
                            :style="{
                              'font-size': priceWidgetPreviewFontSize,
                              'font-family':
                                previewedFont || branding.font_name,
                              background: priceWidgetPreviewColorBackground,
                              color: priceWidgetPreviewColorText,
                            }"
                          >
                            <!-- Rebate -->
                            <div
                              v-if="branding.pricewidget_boxtype !== 'NONE'"
                              style="
                                position: absolute;
                                z-index: 2;
                                border-radius: 50%;
                                display: flex;
                                justify-content: center;
                                align-items: center;
                                font-size: 0.6em;
                                width: 68px;
                                height: 68px;
                              "
                              :style="{
                                top: priceWidgetPreviewRebatePositionTop,
                                right: priceWidgetPreviewRebatePositionRight,
                                color: branding.pricewidget_rebate_color_text,
                                background:
                                  branding.pricewidget_rebate_color_background,
                              }"
                            >
                              -33%
                            </div>
                            <!-- Price -->
                            <div
                              style="
                                display: flex;
                                align-items: baseline;
                                position: relative;
                                top: 0.15em;
                              "
                            >
                              79,<span
                                style="
                                  display: inline-block;
                                  font-size: 0.5em;
                                  position: relative;
                                  top: -0.75em;
                                  left: -0.1em;
                                "
                              >99</span>
                            </div>
                          </div>
                        </v-layout>
                      </branding-field>
                    </v-flex>
                    <v-flex
                      sm6
                      xs12
                      style="margin-top: 32px"
                    >
                      <v-layout align-center>
                        <!-- Pricewidget Text Color -->
                        <branding-colorfield
                          :key="
                            'pricewidget_color_text_' + branding.color_primary
                          "
                          :label="$t('brandingWidgetPrice')"
                          class="mr-2"
                          min-height="48px"
                          max-height="48px"
                          min-width="48px"
                          max-width="48px"
                          style="max-width: 48px"
                          color-picker-id="brandingPricewidgetColorText"
                          :color.sync="branding.pricewidget_color_text"
                          :color-palette="brandingColorPalette"
                          :title="$t('brandingTextColor')"
                        />
                        <!-- Pricewidget Background Color -->
                        <branding-colorfield
                          :key="
                            'pricewidget_color_background_' +
                              branding.color_secondary
                          "
                          min-height="48px"
                          max-height="48px"
                          min-width="48px"
                          max-width="48px"
                          style="max-width: 48px"
                          color-picker-id="brandingPricewidgetColorBackground"
                          :color.sync="branding.pricewidget_color_background"
                          :color-palette="brandingColorPalette"
                          :title="$t('brandingBackgroundColor')"
                        />
                        <v-btn
                          flat
                          icon
                          large
                          color="grey darken-2"
                          style="margin-top: 32px"
                          :title="$t('brandingSwapColors')"
                          @click="
                            swapBrandingColors(
                              'pricewidget_color_text',
                              'pricewidget_color_background'
                            )
                          "
                        >
                          <v-icon medium>
                            swap_horiz
                          </v-icon>
                        </v-btn>
                      </v-layout>
                      <!-- Pricewidget Boxtype -->
                      <v-layout
                        align-center
                        wrap
                        class="mt-2"
                      >
                        <div
                          class="boxtype-btn none redstrikethrough"
                          :class="{
                            selected:
                              branding.pricewidget_boxtype === boxtypes.NONE,
                          }"
                          :title="$t('brandingBoxtypeNone')"
                          @click="branding.pricewidget_boxtype = boxtypes.NONE"
                        />
                        <div
                          class="boxtype-btn rectangle"
                          :class="{
                            selected:
                              branding.pricewidget_boxtype ===
                              boxtypes.RECTANGLE,
                          }"
                          :title="$t('brandingBoxtypeRectangle')"
                          @click="
                            branding.pricewidget_boxtype = boxtypes.RECTANGLE
                          "
                        />
                        <div
                          class="boxtype-btn rounded"
                          :class="{
                            selected:
                              branding.pricewidget_boxtype === boxtypes.ROUNDED,
                          }"
                          :title="$t('brandingBoxtypeRounded')"
                          @click="
                            branding.pricewidget_boxtype = boxtypes.ROUNDED
                          "
                        />
                        <div
                          class="boxtype-btn circle"
                          :class="{
                            selected:
                              branding.pricewidget_boxtype === boxtypes.CIRCLE,
                          }"
                          :title="$t('brandingBoxtypeCircle')"
                          @click="
                            branding.pricewidget_boxtype = boxtypes.CIRCLE
                          "
                        />
                      </v-layout>
                      <v-slide-y-transition>
                        <v-layout
                          v-if="branding.pricewidget_boxtype !== 'NONE'"
                          align-center
                          class="mt-3"
                        >
                          <!-- Pricewidget Rebate Text Color -->
                          <branding-colorfield
                            :key="
                              'pricewidget_rebate_color_text_' +
                                branding.color_primary
                            "
                            :label="$t('brandingWidgetRebate')"
                            class="mr-2"
                            min-height="48px"
                            max-height="48px"
                            min-width="48px"
                            max-width="48px"
                            style="max-width: 48px"
                            color-picker-id="brandingPricewidgetColorText"
                            :color.sync="branding.pricewidget_rebate_color_text"
                            :color-palette="brandingColorPalette"
                            :disabled="branding.pricewidget_boxtype === 'NONE'"
                            :title="$t('brandingTextColor')"
                          />
                          <!-- Pricewidget Rebate Background Color -->
                          <branding-colorfield
                            :key="
                              'pricewidget_rebate_color_background_' +
                                branding.color_secondary
                            "
                            min-height="48px"
                            max-height="48px"
                            min-width="48px"
                            max-width="48px"
                            style="max-width: 48px"
                            color-picker-id="brandingPricewidgetColorBackground"
                            :color.sync="
                              branding.pricewidget_rebate_color_background
                            "
                            :color-palette="brandingColorPalette"
                            :disabled="branding.pricewidget_boxtype === 'NONE'"
                            :title="$t('brandingBackgroundColor')"
                          />
                          <v-btn
                            :disabled="branding.pricewidget_boxtype === 'NONE'"
                            flat
                            icon
                            large
                            color="grey darken-2"
                            style="margin-top: 32px"
                            :title="$t('brandingSwapColors')"
                            @click="
                              swapBrandingColors(
                                'pricewidget_rebate_color_text',
                                'pricewidget_rebate_color_background'
                              )
                            "
                          >
                            <v-icon medium>
                              swap_horiz
                            </v-icon>
                          </v-btn>
                        </v-layout>
                      </v-slide-y-transition>
                    </v-flex>
                  </v-layout>
                  <v-layout
                    class="mt-2 mb-3"
                    wrap
                  >
                    <!-- Buttonwidget Preview -->
                    <v-flex
                      sm6
                      xs12
                      style="pointer-events: none"
                    >
                      <branding-field
                        :border="false"
                        :label="$t('brandingWidgetButton')"
                        :class="{'mr-3': $vuetify.breakpoint.smAndUp}"
                        min-height="200px"
                        max-height="200px"
                        :title="$t('brandingPreview')"
                      >
                        <v-layout
                          slot="field"
                          align-center
                          justify-center
                          :style="{background: branding.color_background}"
                        >
                          <div
                            class="boxtype"
                            :class="[
                              branding.buttonwidget_boxtype.toLowerCase(),
                            ]"
                            style="
                              min-width: 128px;
                              min-height: 128px;
                              line-height: 72px;
                              display: flex;
                              justify-content: center;
                              align-items: center;
                              font-weight: bold;
                              transform: scale(1.25);
                            "
                            :style="{
                              'font-size': priceWidgetPreviewFontSize,
                              'font-family':
                                previewedFont || branding.font_name,
                              background: buttonWidgetPreviewColorBackground,
                              color: buttonWidgetPreviewColorText,
                            }"
                          >
                            SALE
                          </div>
                        </v-layout>
                      </branding-field>
                    </v-flex>
                    <v-flex
                      sm6
                      xs12
                      style="margin-top: 32px"
                    >
                      <v-layout align-center>
                        <!-- Buttonwidget Text Color -->
                        <branding-colorfield
                          :key="
                            'buttonwidget_color_text_' + branding.color_primary
                          "
                          no-label
                          class="mr-2"
                          min-height="48px"
                          max-height="48px"
                          min-width="48px"
                          max-width="48px"
                          style="max-width: 48px"
                          color-picker-id="brandingButtonwidgetColorBackground"
                          :color.sync="branding.buttonwidget_color_text"
                          :color-palette="brandingColorPalette"
                          :title="$t('brandingTextColor')"
                        />
                        <!-- Buttonwidget Background Color -->
                        <branding-colorfield
                          :key="
                            'buttonwidget_color_background_' +
                              branding.color_secondary
                          "
                          no-label
                          min-height="48px"
                          max-height="48px"
                          min-width="48px"
                          max-width="48px"
                          style="max-width: 48px"
                          color-picker-id="brandingButtonwidgetColorBackground"
                          :color.sync="branding.buttonwidget_color_background"
                          :color-palette="brandingColorPalette"
                          :title="$t('brandingBackgroundColor')"
                        />
                        <v-btn
                          flat
                          icon
                          large
                          color="grey darken-2"
                          :title="$t('brandingSwapColors')"
                          @click="
                            swapBrandingColors(
                              'buttonwidget_color_text',
                              'buttonwidget_color_background'
                            )
                          "
                        >
                          <v-icon medium>
                            swap_horiz
                          </v-icon>
                        </v-btn>
                      </v-layout>
                      <!-- Buttonwidget Boxtype -->
                      <v-layout
                        align-center
                        wrap
                        class="mt-2"
                      >
                        <div
                          class="boxtype-btn none redstrikethrough"
                          :class="{
                            selected:
                              branding.buttonwidget_boxtype === boxtypes.NONE,
                          }"
                          :title="$t('brandingBoxtypeNone')"
                          @click="branding.buttonwidget_boxtype = boxtypes.NONE"
                        />
                        <div
                          class="boxtype-btn rectangle"
                          :class="{
                            selected:
                              branding.buttonwidget_boxtype ===
                              boxtypes.RECTANGLE,
                          }"
                          :title="$t('brandingBoxtypeRectangle')"
                          @click="
                            branding.buttonwidget_boxtype = boxtypes.RECTANGLE
                          "
                        />
                        <div
                          class="boxtype-btn rounded"
                          :class="{
                            selected:
                              branding.buttonwidget_boxtype ===
                              boxtypes.ROUNDED,
                          }"
                          :title="$t('brandingBoxtypeRounded')"
                          @click="
                            branding.buttonwidget_boxtype = boxtypes.ROUNDED
                          "
                        />
                        <div
                          class="boxtype-btn circle"
                          :class="{
                            selected:
                              branding.buttonwidget_boxtype === boxtypes.CIRCLE,
                          }"
                          :title="$t('brandingBoxtypeCircle')"
                          @click="
                            branding.buttonwidget_boxtype = boxtypes.CIRCLE
                          "
                        />
                      </v-layout>
                    </v-flex>
                  </v-layout>
                </v-layout>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <!-- Step 1 Buttons -->
          <v-layout
            v-if="step == 1"
            align-center
            wrap
          >
            <v-btn
              round
              class="ml-auto msr white--text"
              :loading="loading"
              :disabled="!step1Valid"
              data-cy="step-1-go-forward-btn"
              @click="step = 2"
            >
              <v-icon left>
                arrow_forward
              </v-icon>
              {{ $t('goForward') }}
            </v-btn>
          </v-layout>
          <!-- Step 2 Buttons -->
          <v-layout
            v-if="step == 2"
            align-center
          >
            <v-btn
              flat
              round
              class="mr-auto"
              color="grey darken-3"
              @click="step = 1"
            >
              <v-icon left>
                arrow_back
              </v-icon>
              {{ $t('goBack') }}
            </v-btn>
            <v-btn
              round
              class="ml-auto msr white--text"
              :disabled="!step2Valid"
              data-cy="step-2-go-forward-btn"
              @click="step = 3"
            >
              <v-icon left>
                arrow_forward
              </v-icon>
              {{ $t('goForward') }}
            </v-btn>
          </v-layout>
          <!-- Step 3 Buttons -->
          <v-layout
            v-if="step == 3"
            align-center
          >
            <v-btn
              round
              class="mr-auto"
              flat
              color="grey darken-3"
              @click="step = 2"
            >
              <v-icon left>
                arrow_back
              </v-icon>
              {{ $t('goBack') }}
            </v-btn>
            <v-btn
              round
              class="ml-auto msr white--text"
              :disabled="!step3Valid"
              data-cy="step-3-save-btn"
              @click="save()"
            >
              <v-icon left>
                check
              </v-icon>
              {{ $t('save') }}
            </v-btn>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import isEqual from 'lodash.isequal';

import {isLightColor, deepCopyObject} from '@/utils';

import {createColorPaletteFromBranding} from '@/mixins/colorPalette.js';
import {getFontPreviewScaleModifierByFontName} from '@/mixins/fonts.js';

import BrandingColorfield from '@/components/BrandingColorfield';
import BrandingField from '@/components/BrandingField';
import BrandingFontfield from '@/components/BrandingFontfield';
import BrandingLogofield from '@/components/BrandingLogofield';

export const boxtypes = {
  NONE: 'NONE',
  RECTANGLE: 'RECTANGLE',
  ROUNDED: 'ROUNDED',
  CIRCLE: 'CIRCLE',
};

const defaults = {
  loading: false,
  busy: false,
  step: 1,
};

export default {
  components: {
    BrandingColorfield,
    BrandingField,
    BrandingFontfield,
    BrandingLogofield,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    branding: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      dialogCardRenderKey: 0,

      // Hold unchanged initial Branding state to detect changes.
      initialBranding: null,

      boxtypes: boxtypes,

      loading: defaults.loading,
      busy: defaults.busy,
      step: defaults.step,

      previewedFont: null,
      brandingLogoRenderKey: 0,
      brandingBackgroundBranchImageUrl: null,
      brandingNameMaxLength: 64,
    };
  },
  computed: {
    /**
     * Whether Branding object has been changed since the dialog has been opened.
     */
    dirty() {
      if (this.initialBranding === null) {
        return true;
      }
      return !isEqual(this.initialBranding, this.branding);
    },
    brandings() {
      return this.$store.state.common.brandings;
    },
    branches() {
      return this.$store.state.common.branches;
    },
    priceWidgetPreviewRebatePositionTop() {
      let value = -1.2;
      if (this.branding.pricewidget_boxtype === 'CIRCLE') {
        value += 0.2;
      }
      return value + 'em';
    },
    priceWidgetPreviewRebatePositionRight() {
      return this.priceWidgetPreviewRebatePositionTop;
    },
    priceWidgetPreviewColorBackground() {
      if (this.branding.pricewidget_boxtype === boxtypes.NONE) {
        return null;
      }
      return this.branding.pricewidget_color_background;
    },
    priceWidgetPreviewColorText() {
      if (this.branding.pricewidget_boxtype === boxtypes.NONE) {
        return this.branding.pricewidget_color_background;
      }
      return this.branding.pricewidget_color_text;
    },
    priceWidgetPreviewFontSize() {
      const value = 2.75;
      const scaleModifier = getFontPreviewScaleModifierByFontName(
        this.previewedFont || this.branding.font_name
      );
      return value * scaleModifier + 'em';
    },
    buttonWidgetPreviewColorBackground() {
      if (this.branding.buttonwidget_boxtype === boxtypes.NONE) {
        return null;
      }
      return this.branding.buttonwidget_color_background;
    },
    buttonWidgetPreviewColorText() {
      if (this.branding.buttonwidget_boxtype === boxtypes.NONE) {
        return this.branding.buttonwidget_color_background;
      }
      return this.branding.buttonwidget_color_text;
    },
    /**
     * Handle this.logo being either a URL String or a cropped image Blob.
     */
    brandingLogoUrl() {
      if (this.branding.logo === null) {
        return null;
      }
      if (this.branding.logo instanceof Blob) {
        return URL.createObjectURL(this.branding.logo);
      }
      // It's a URL already.
      return this.branding.logo;
    },
    brandingColorPalette() {
      return createColorPaletteFromBranding(this.branding);
    },
    isNewBranding() {
      return this.branding && !this.branding.id;
    },
    hasChosenColors() {
      return (
        !!this.branding &&
        !!this.branding.color_primary &&
        !!this.branding.color_secondary
      );
    },
    step1Valid() {
      return (
        !!this.branding &&
        this.branding.name.trim() !== '' &&
        this.branding.name.length <= this.brandingNameMaxLength &&
        this.branding.branch_id &&
        this.hasChosenColors
      );
    },
    step2Valid() {
      // return this.step1Valid && !!this.branding.logo;
      return this.step1Valid;
    },
    step3Valid() {
      // return (
      //   this.step2Valid &&
      //   !!this.branding.color_background &&
      //   !!this.branding.color_headline &&
      //   !!this.branding.pricewidget_color_text &&
      //   !!this.branding.pricewidget_color_background &&
      //   !!this.branding.pricewidget_boxtype &&
      //   !!this.branding.buttonwidget_color_text &&
      //   !!this.branding.buttonwidget_color_background &&
      //   !!this.branding.buttonwidget_boxtype
      // );
      return this.step2Valid;
    },
  },
  watch: {
    show: function (show) {
      this.dialogCardRenderKey += 1;

      // Focus namefield when creating a new Branding.
      if (this.isNewBranding) {
        this.$nextTick(() => this.$refs.namefield.focus());
      }
      // Reset when closing dialog.
      if (!show) {
        this.reset();
      }
    },
    /**
     * Cache initial Branding state to detect changes.
     */
    branding: function (branding) {
      if (branding !== null) {
        this.initialBranding = deepCopyObject(branding);
      } else {
        this.initialBranding = null;
      }
    },
    'branding.branch_id': function (branchId) {
      this.brandingBackgroundBranchImageUrl = null;
      if (branchId) {
        this.fetchBrandingBackgroundBranchImageUrl(branchId);
      }
    },
  },
  created() {
    this.fetchBranches();
  },
  methods: {
    fetchBranches() {
      this.loading = true;
      this.$store
        .dispatch('LIST_BRANCHES')
        .finally(() => (this.loading = false));
    },
    fetchBrandingBackgroundBranchImageUrl(branchId) {
      return this.$api
        .retrieveBrandingBackgroundBranchImageUrl(branchId)
        .then((response) => {
          this.brandingBackgroundBranchImageUrl = response.data;
        });
    },
    onPrimaryColorChanged() {
      this.branding.pricewidget_rebate_color_background =
        this.branding.color_primary;

      const textColor = isLightColor(this.branding.color_primary)
        ? '#000000'
        : '#ffffff';
      this.branding.pricewidget_rebate_color_text = textColor;
    },
    onSecondaryColorChanged() {
      this.branding.pricewidget_color_background =
        this.branding.color_secondary;
      this.branding.buttonwidget_color_background =
        this.branding.color_secondary;

      const textColor = isLightColor(this.branding.color_secondary)
        ? '#000000'
        : '#ffffff';
      this.branding.pricewidget_color_text = textColor;
      this.branding.buttonwidget_color_text = textColor;
    },
    onBackgroundColorChanged() {
      const textColor = isLightColor(this.branding.color_background)
        ? '#000000'
        : '#ffffff';
      this.branding.color_headline = textColor;
    },
    swapBrandingColors(color1, color2) {
      const temp = this.branding[color1];
      this.branding[color1] = this.branding[color2];
      this.branding[color2] = temp;
    },
    onBrandingImageCropped(payload) {
      this.branding.logo = payload.file;
      // Setting a new Blob does not trigger reactivity, so force a rerender.
      this.brandingLogoRenderKey += 1;
    },
    reset() {
      this.loading = defaults.loading;
      this.busy = defaults.busy;
      this.step = defaults.step;
    },
    closeDialog() {
      this.$emit('update:show', false);
    },
    askAbort() {
      if (this.dirty) {
        const confirmed = confirm(this.$t('brandingDialogAbortQuestion'));
        if (!confirmed) {
          return;
        }
      }
      this.abort();
    },
    abort() {
      this.$emit('abort');
      this.closeDialog();
    },
    save() {
      this.$emit('save', this.branding);
      this.closeDialog();
    },
  },
};
</script>

<style scoped>
.redstrikethrough {
  display: inline-block;
  position: relative;
}
.redstrikethrough::before {
  content: '';
  position: absolute;
  left: 0px;
  right: 0px;
  top: 0px;
  bottom: 0px;
  background: linear-gradient(
    to left top,
    transparent 46.5%,
    #ff5e5e 47%,
    #ff5e5e 52%,
    transparent 53%
  );
  pointer-events: none;
}

/* Preview of boxtype. */
.boxtype.none {
  border: none;
  background: none;
}
.boxtype.rectangle {
  border-radius: 0;
}
.boxtype.rounded {
  border-radius: 16px;
}
.boxtype.circle {
  min-width: 128px !important;
  min-height: 128px !important;
  line-height: 100px !important;
  border-radius: 50%;
}

/* Buttons that change the boxtype. */

.boxtype-btn.selected {
  border: 2px solid var(--msr-color-primary) !important;
}
.boxtype-btn {
  cursor: pointer;
  min-width: 42px;
  min-height: 42px;
  border: 1px solid rgba(0, 0, 0, 0.16);
  background: rgba(0, 0, 0, 0.08);
  margin-left: 4px;
  margin-right: 10px;
}
.boxtype-btn.none {
  border-color: rgba(0, 0, 0, 0.04);
  background: rgba(0, 0, 0, 0.04);
}
.boxtype-btn.rectangle {
  border-radius: 0;
}
.boxtype-btn.rounded {
  border-radius: 12px;
}
.boxtype-btn.circle {
  border-radius: 50%;
}
</style>



// WEBPACK FOOTER //
// src/components/CreateEditBrandingDialog.vue